import type { CityConfig } from "../city-template/types";

export const appletonConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Wisconsin Department of Health Services Vital Records office. Online, mail, or in-person options for Outagamie County and Fox Valley residents.", url: "https://www.dhs.wisconsin.gov/vitalrecords/birth.htm" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through Wisconsin Vital Records. Required for estate and legal matters in Outagamie County.", url: "https://www.dhs.wisconsin.gov/vitalrecords/death.htm" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Outagamie County Clerk at 320 S Walnut St. Certified copies for name changes and passport applications.", url: "https://www.outagamie.org/government/county-clerk" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Outagamie County Clerk of Circuit Court. Required for name change passport corrections.", url: "https://www.wicourts.gov/courts/circuit/outagamie.htm" },
  ],
  city: "Appleton",
  state: "Wisconsin",
  stateAbbr: "WI",
  slug: "appleton",
  route: "/appleton",
  title: "Emergency Passport Appleton, WI — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Appleton, Wisconsin — as fast as 24 hours. Fast passport renewal and new passport applications for Appleton, Neenah, Menasha, and all Outagamie County residents. A+ BBB rated registered courier with access to Chicago Passport Agency.",
  h1: "Emergency & Expedited Passport Services in Appleton, WI",
  heroSubtitle: "Appleton and the Fox Valley have no local passport agency — the nearest is Chicago, about 200 miles south. Fast Passport Center's registered couriers deliver your documents directly to the Chicago Passport Agency, getting you a new or renewed passport as fast as 24–48 hours without the 7-hour roundtrip drive.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Appleton%20Wisconsin%20downtown%20skyline%20along%20the%20Fox%20River%20with%20historic%20paper%20mill%20buildings%20converted%20to%20modern%20spaces%2C%20Lawrence%20University%20campus%20with%20classic%20brick%20architecture%2C%20vibrant%20College%20Avenue%20with%20local%20shops%20and%20restaurants%2C%20autumn%20colors%20reflecting%20on%20calm%20river%20water%2C%20warm%20golden%20afternoon%20light%2C%20professional%20cityscape%20photography%20with%20rich%20amber%20tones%2C%20classic%20Midwestern%20college%20town%20charm%2C%20no%20text%20or%20people%2C%20editorial%20style%20urban%20photography&width=1800&height=700&seq=appleton-wi-hero-001&orientation=landscape",
  heroImageAlt: "Appleton Wisconsin downtown skyline along the Fox River with historic architecture",
  geoPosition: "44.2619;-88.4154",
  geoPlacename: "Appleton, Wisconsin",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Approximately 200 miles from Appleton — about 3.5 hours via I-41 and I-94 south",
  },
  nearbyCitiesZips: [
    { name: "Downtown Appleton", zips: "54911, 54914" },
    { name: "Neenah", zips: "54956" },
    { name: "Menasha", zips: "54952" },
    { name: "Kaukauna", zips: "54130" },
    { name: "Little Chute", zips: "54140" },
    { name: "Kimberly", zips: "54136" },
    { name: "Grand Chute", zips: "54913, 54914" },
    { name: "Greenville", zips: "54942" },
    { name: "Hortonville", zips: "54944" },
    { name: "Oshkosh", zips: "54901, 54902" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Registered courier to Chicago Passport Agency — no 7-hour roundtrip from Appleton required" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting for the Fox Valley" },
    { icon: "ri-building-line", title: "Fox Valley Hub", desc: "Serving Appleton, Neenah, Menasha, Kaukauna, and all Outagamie and Winnebago counties" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels from any UPS or FedEx location in the Fox Cities area" },
  ],
  internalLinks: [
    { name: "Expedited passport in Green Bay, WI", route: "/green-bay" },
    { name: "Emergency passport help in Milwaukee, WI", route: "/milwaukee" },
    { name: "Urgent passport services in Madison, WI", route: "/madison-wi" },
    { name: "Wisconsin passport services overview", route: "/wisconsin" },
  ],
  fpcOfficeAddress: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
  fpcOfficeNote: "Fast Passport Center serves Appleton, Wisconsin via secure registered courier to the Chicago Passport Agency (230 S Dearborn St, Chicago). Approximately 200 miles — about a 3.5-hour drive via I-41 and I-94. Appleton's position in the heart of the Fox Valley, with direct interstate access, makes it perfectly positioned for expedited service. We handle the Chicago drive so Appleton and Fox Cities residents never need to navigate downtown Chicago traffic.",
  fpcOfficeDistance: "Serving Appleton, Neenah, Menasha, Kaukauna, Little Chute, Kimberly, Grand Chute, Greenville, Hortonville, Oshkosh, and all Fox Valley communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Appleton and the Fox Valley. From downtown College Avenue and the Lawrence University campus to surrounding communities like Neenah, Menasha, and Kaukauna — we serve all of Outagamie County and the Fox Cities region with direct courier access to the Chicago Passport Agency.",
  nearbyCitiesIntro: "We serve Appleton and all nearby Fox Valley communities including Neenah, Menasha, Kaukauna, Little Chute, Kimberly, Grand Chute, Greenville, Hortonville, and Oshkosh. Appleton's central Fox Valley location makes it an ideal hub for expedited passport services across northeast Wisconsin.",
  howItWorksIntro: "Most Appleton travelers — especially Fox Valley business professionals and Lawrence University community members — complete everything online and ship documents via overnight courier from any UPS or FedEx in the Fox Cities. Our registered couriers handle the Chicago Passport Agency delivery so you can focus on your travel plans.",
  mistakesIntro: "Appleton sits in the heart of Wisconsin's Fox Valley with a strong international business community — but that doesn't mean getting a passport is quick through normal channels. Here are the most common mistakes we see from Fox Cities travelers.",
  testimonialsIntro: "Appleton travelers — from Fox Valley professionals to Lawrence University faculty to families throughout the region — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Appleton, WI? Here are the most common ones we hear from Fox Valley and Outagamie County travelers.",
  statePageRoute: "/states/wisconsin",
  statePageName: "Wisconsin",
  dmvWebsite: "https://www.dot.wisconsin.gov/drivers",
  dmvLabel: "Wisconsin DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Chicago Agency courier — no long drive required", "Predictable turnaround for Fox Valley travelers", "Ideal for ATW Airport departures", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service USPS", "Good for planned business and family travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Drive 200 miles to Chicago Passport Agency", "7-hour roundtrip from Appleton"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Appleton travelers with immediate departure needs — as fast as 24 hours. Our couriers hand-deliver to the Chicago Passport Agency so you skip the 7-hour roundtrip.",
      bullets: ["24–48 hour turnaround available", "Chicago Passport Agency — courier handles the 3.5-hr drive", "Fox Valley business and family travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Appleton adults with a passport issued within the last 15 years. Skip the post office appointment — ideal for Fox Valley professionals and commuters.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to your Fox Valley address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Appleton residents, Fox Valley families, and Lawrence University students in the Fox Cities area.",
      bullets: ["Acceptance facility scheduling near Appleton", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Appleton residents — whether lost at home, during a move, or while preparing for travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Appleton and Fox Valley families traveling internationally with kids.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Appleton and Outagamie County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Appleton WI", description: "Urgent passport processing for Appleton Wisconsin travelers via Chicago Passport Agency courier." },
    { name: "Expedited Passport Renewal Appleton WI", description: "Fast passport renewal for Appleton and Fox Valley Wisconsin residents." },
    { name: "First-Time Passport Appleton WI", description: "New passport application services for Appleton Wisconsin residents and Fox Cities families." },
    { name: "Child Passport Appleton WI", description: "Minor passport services for Appleton Wisconsin families with children under 16." },
  ],
  testimonials: [
    {
      name: "Rebecca M.",
      location: "Appleton, WI — Near College Avenue",
      rating: 5,
      text: "Business trip to Germany with 5 days notice and my passport had expired. Fast Passport Center got me sorted in 48 hours — the courier to Chicago was a lifesaver. No way I was driving 7 hours roundtrip.",
      service: "Emergency Passport Renewal",
    },
    {
      name: "Tom H.",
      location: "Neenah, WI",
      rating: 5,
      text: "Family trip to Ireland and we discovered our kids' passports were expired 10 days before departure. Fast Passport Center walked us through the child renewal and all three arrived in 4 days. Incredible service.",
      service: "Family Passport Bundle",
    },
    {
      name: "Lauren K.",
      location: "Appleton, WI — Lawrence University Area",
      rating: 5,
      text: "First-time passport as a Lawrence student heading to study abroad in Spain. Fast Passport Center made the whole process easy — from scheduling my acceptance facility appointment to getting the passport in 5 days.",
      service: "First-Time Passport",
    },
    {
      name: "Dan S.",
      location: "Kaukauna, WI",
      rating: 5,
      text: "Lost my passport somewhere between our move from Menasha to Kaukauna. Had a flight to Mexico City in 6 days. Fast Passport Center replaced it in 48 hours. The courier to Chicago made all the difference.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "appleton-wi-1",
      name: "Appleton Downtown Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "200 N Drew St",
      city: "Appleton",
      state: "WI",
      zip: "54911",
      phone: "(920) 380-0750",
      hours: "Mon–Fri: 8:30 AM – 5:30 PM",
      distance: "Downtown Appleton, near College Avenue",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary Fox Valley acceptance facility. Appointments recommended — call ahead. Busy during summer travel season.",
      icon: "ri-mail-line",
    },
    {
      id: "appleton-wi-2",
      name: "Outagamie County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "320 S Walnut St",
      city: "Appleton",
      state: "WI",
      zip: "54911",
      phone: "(920) 832-5077",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Downtown Appleton, near courthouse",
      website: "https://www.outagamie.org/government/county-clerk",
      websiteLabel: "Outagamie County",
      notes: "County clerk passport acceptance. Convenient downtown location. No photo services on-site — bring photos.",
      icon: "ri-government-line",
    },
    {
      id: "appleton-wi-3",
      name: "Neenah Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "225 W Doty Ave",
      city: "Neenah",
      state: "WI",
      zip: "54956",
      phone: "(920) 722-0385",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "Approximately 7 miles south of downtown Appleton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Neenah and Menasha residents. Good alternative when Appleton appointments are full.",
      icon: "ri-mail-line",
    },
    {
      id: "appleton-wi-4",
      name: "Kaukauna Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "127 W Wisconsin Ave",
      city: "Kaukauna",
      state: "WI",
      zip: "54130",
      phone: "(920) 766-3100",
      hours: "Mon–Fri: 9:00 AM – 5:00 PM",
      distance: "Approximately 10 miles northeast of downtown Appleton",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Eastern Fox Valley facility. Convenient for Kaukauna, Little Chute, and Kimberly residents.",
      icon: "ri-mail-line",
    },
    {
      id: "appleton-wi-5",
      name: "Oshkosh Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 N Main St",
      city: "Oshkosh",
      state: "WI",
      zip: "54901",
      phone: "(920) 236-8000",
      hours: "Mon–Fri: 9:00 AM – 5:30 PM",
      distance: "Approximately 20 miles south of Appleton, in Oshkosh",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Southern Fox Valley facility serving Oshkosh and Winnebago County. Good alternative when Appleton facilities are booked.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, 2–3 week wait", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 3.5-hr drive to Chicago via I-41/I-94", fpc: "No — ship from Appleton, courier handles it" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Fox Valley Business Travel", agency: "No special support for professionals", fpc: "Experienced with Fox Valley business travelers" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Appleton residents often underestimate passport processing times. Even though the Fox Valley has a strong international business presence, there's no local passport agency. Routine processing takes 8–11 weeks. Start expedited service at least 6 weeks before travel.",
    },
    {
      icon: "ri-car-line",
      title: "Driving to Chicago Without an Appointment",
      description: "The Chicago Passport Agency is 200 miles away — a 7-hour roundtrip from Appleton. Showing up without a pre-scheduled appointment is a wasted trip. Our courier service eliminates this risk entirely.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Passport Photo",
      description: "Photos from retail locations in the Fox Valley sometimes fail Department of State standards. Photos must be exactly 2x2 inches on a white background — no smiling with teeth showing, no glasses. Always verify before submission.",
    },
    {
      icon: "ri-file-list-line",
      title: "Using Wrong Form (DS-82 vs DS-11)",
      description: "Fox Valley residents often grab the wrong form. DS-82 is for adult renewals by mail. First-time applications, lost passports, and child passports require DS-11 and an in-person acceptance facility visit.",
    },
    {
      icon: "ri-calendar-line",
      title: "Not Accounting for Summer Travel Rush",
      description: "The Fox Valley sees heavy passport demand May–July as families prepare for summer vacations. Appleton-area acceptance facilities fill 3–4 weeks in advance. Plan ahead.",
    },
    {
      icon: "ri-global-line",
      title: "Not Checking 6-Month Validity Rule",
      description: "Many countries require 6 months of passport validity beyond your stay. Fox Valley business travelers to Europe and Asia frequently discover this requirement too late.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from anywhere in Appleton or the Fox Cities." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop at any UPS or FedEx in the Fox Valley." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for Fox Valley documentation errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Our registered courier hand-delivers to the Chicago Passport Agency — no need to make the 7-hour roundtrip." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Appleton address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Appleton, WI?", a: "Fast Passport Center offers emergency passport processing in Appleton, Wisconsin in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Chicago Passport Agency (230 S Dearborn St, Chicago) — no need to make the 7-hour roundtrip drive yourself." },
    { q: "Does Appleton have its own passport agency?", a: "No — Appleton does not have a regional passport agency, and neither does any city in Wisconsin. The nearest is the Chicago Passport Agency, approximately 200 miles south (about a 3.5-hour drive via I-41 and I-94). Fast Passport Center's courier service means Fox Valley residents never need to make that trip." },
    { q: "Is Appleton well-positioned for expedited passport service?", a: "Appleton's location on the I-41 corridor with direct interstate access to Chicago makes overnight shipping fast and reliable. While it's a 3.5-hour drive each way, our courier service handles the Chicago delivery so you don't have to leave the Fox Valley." },
    { q: "What airport do Appleton travelers use for international flights?", a: "Appleton International Airport (ATW) offers domestic connections to major hubs. Most international travelers from the Fox Valley connect through Chicago O'Hare (ORD), about 180 miles south, or fly from Milwaukee Mitchell (MKE), about 100 miles south." },
    { q: "How much does an expedited passport cost in Appleton?", a: "Government fees are fixed: $130 for a first-time adult passport or $130 + $60 expedite fee for a renewed adult passport book. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your specific departure timeline." },
    { q: "Where can I get passport photos in Appleton?", a: "Walgreens (multiple Appleton locations including 2621 E College Ave), CVS (700 W Wisconsin Ave), and the UPS Store (511 W College Ave) all offer passport photo services. Photos must be 2x2 inches on a white background, taken within 6 months." },
    { q: "Does Fast Passport Center serve Lawrence University students and faculty?", a: "Yes. We serve all Lawrence University students, faculty, and staff. We handle study abroad passport needs, faculty research travel, and international conference attendance with turnaround times as fast as 24–48 hours." },
    { q: "What about Neenah, Menasha, or Kaukauna residents?", a: "Absolutely. Fast Passport Center serves all of the Fox Valley including Appleton, Neenah, Menasha, Kaukauna, Little Chute, Kimberly, Grand Chute, Greenville, Hortonville, and Oshkosh. Our courier service works the same for everyone throughout the region." },
  ],
  photoLocations: [
    { name: "Walgreens — Appleton (College Ave)", address: "2621 E College Ave, Appleton, WI 54915", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/" },
    { name: "CVS — Appleton (Wisconsin Ave)", address: "700 W Wisconsin Ave, Appleton, WI 54914", hours: "Mon–Sun: 8AM–9PM", website: "https://www.cvs.com/" },
    { name: "UPS Store — Appleton", address: "511 W College Ave, Appleton, WI 54911", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM", website: "https://www.theupsstore.com/" },
    { name: "Walgreens — Neenah", address: "1005 S Green Bay Rd, Neenah, WI 54956", hours: "Mon–Sun: 7AM–10PM", website: "https://www.walgreens.com/" },
  ],
  dmvLocations: [
    { name: "WI DMV — Appleton", address: "3600 E Calumet St, Appleton, WI 54915", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Fox Valley DMV serving Appleton and Outagamie County. REAL ID and driver's license renewals. Appointment recommended.", featured: true },
    { name: "WI DMV — Oshkosh", address: "300 S Koeller St, Oshkosh, WI 54902", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Winnebago County DMV. Alternative when Appleton is busy.", featured: false },
  ],
  airports: [
    {
      code: "ATW",
      name: "Appleton International Airport",
      distance: "5 miles west of downtown Appleton",
      address: "W6390 Challenger Dr, Appleton, WI 54914",
      note: "Appleton's regional airport with domestic service to major hubs. International travelers typically connect through ORD, MSP, or DTW. A valid passport is required for all international connections.",
      flightUrl: "https://www.atwairport.com/",
      flightLabel: "ATW Airport",
      featured: true,
    },
    {
      code: "ORD",
      name: "Chicago O'Hare International Airport",
      distance: "180 miles south of Appleton — approximately 3 hours",
      address: "10000 W O'Hare Ave, Chicago, IL 60666",
      note: "Major international hub used by many Fox Valley travelers for overseas flights. Extensive international routes across six continents.",
      flightUrl: "https://www.flychicago.com/ohare/",
      flightLabel: "O'Hare Airport",
      featured: false,
    },
    {
      code: "MKE",
      name: "Milwaukee Mitchell International Airport",
      distance: "100 miles south of Appleton — approximately 1.5 hours",
      address: "5300 S Howell Ave, Milwaukee, WI 53207",
      note: "Alternative international airport for Fox Valley travelers. Seasonal international routes plus domestic connections.",
      flightUrl: "https://www.mitchellairport.com/",
      flightLabel: "MKE Airport",
      featured: false,
    },
  ],
};